import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

export interface SignaturePadHandle {
    getSignature: () => string;
    isEmpty: () => boolean;
    clear: () => void;
}

interface SignaturePadProps {
    onChange?: (hasSignature: boolean) => void;
}

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(({ onChange }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
    const [hasSignature, setHasSignature] = useState(false);

    useImperativeHandle(ref, () => ({
        getSignature: () => canvasRef.current?.toDataURL('image/png') ?? '',
        isEmpty: () => !hasSignature,
        clear: () => clearCanvas(),
    }));

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
    }, []);

    const getPos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): { x: number; y: number } => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        if ('touches' in e) {
            const rect = canvas.getBoundingClientRect();
            const touch = e.touches[0];
            return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
        }
        return { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        setLastPos(getPos(e));
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        const newPos = getPos(e);

        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';

        ctx.beginPath();
        ctx.moveTo(lastPos.x, lastPos.y);
        ctx.lineTo(newPos.x, newPos.y);
        ctx.stroke();

        setLastPos(newPos);
        if (!hasSignature) {
            setHasSignature(true);
            onChange?.(true);
        }
    };

    const endDrawing = () => setIsDrawing(false);

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
        onChange?.(false);
    };

    return (
        <div className="w-full">
            <label className="block mb-2 text-sm font-medium text-gray-700">
                Draw your signature below
            </label>
            <div className="relative w-full">
                <canvas
                    ref={canvasRef}
                    className="bg-gray-50 border border-gray-300 w-full h-36 rounded-lg touch-none"
                    style={{ touchAction: 'none' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={endDrawing}
                    onMouseOut={endDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={(e) => {
                        e.preventDefault();
                        draw(e);
                    }}
                    onTouchEnd={endDrawing}
                    onTouchCancel={endDrawing}
                />
                <button
                    type="button"
                    onClick={clearCanvas}
                    title="Clear Signature"
                    className="absolute bottom-2 right-2 px-2 py-1 bg-white border border-gray-300 shadow hover:scale-110 rounded-lg transition-transform"
                >
                    <svg className="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">Use your mouse or finger to sign.</p>
        </div>
    );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
